import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  alpha,
  Modal,
  IconButton,
} from '@mui/material';
import { motion, AnimatePresence } from 'motion/react';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import bmwDetailImage from 'figma:asset/d30ec8207a5d83317439cd1576ab790533ca9acf.webp';
import mercedesRoomImage from 'figma:asset/f370bc94787c14d2317b94673d0634ec38cf7965.webp';
import privateLoungeImage from '../../imports/private-lounge.webp';
import gtrInteriorImage from 'figma:asset/50be18a9b4d3d41b72e353b6d0894e636b25bff1.webp';
import amgDetailImage from 'figma:asset/09433cfdb6cef19df4a23efcf57e4bae5be903d8.webp';
import highPileRugImage from 'figma:asset/d960ea2ae017c9c517fff20cc02106b3c07d8387.webp';
import carpetCleaningImage from 'figma:asset/78a4639ad94e671ed42eefd1d3f386ab3118cd06.webp';
import tileStoneImage from 'figma:asset/0469592e0f23d0e2da5ee8b6b5787ba05f83e37d.webp';
import newStoneImage from 'figma:asset/3aa17a1b24c48e260e8b003b56475acb996d5b31.webp';
import yachtDeckImage from 'figma:asset/0eddc9547924722edb4bd32f9660c5b9ce9856e7.webp';
import yachtProfileImage from 'figma:asset/e42e05614c1aac65f21e8377b92681ba073e1674.webp';

const gold = '#C0202A';
const silver = '#C0C0C0';

const allImages = [
  { src: amgDetailImage, category: 'auto', title: 'Mercedes AMG — Paint Correction & Detail', aspect: 'landscape' },
  { src: bmwDetailImage, category: 'auto', title: 'BMW M6 — Exterior Polish & Protection', aspect: 'landscape' },
  { src: gtrInteriorImage, category: 'auto', title: 'Mercedes GTR — Interior Restoration', aspect: 'portrait' },
  { src: mercedesRoomImage, category: 'auto', title: 'Mercedes AMG — Showroom Preparation', aspect: 'landscape' },
  { src: privateLoungeImage, category: 'interior', title: 'Luxury Lounge — Leather & Stone Detail', aspect: 'landscape' },
  { src: carpetCleaningImage, category: 'interior', title: 'Residential Carpet — Deep Extraction Cleaning', aspect: 'landscape' },
  { src: highPileRugImage, category: 'interior', title: 'Area Rug — High-Pile Luxury Restoration', aspect: 'portrait' },
  { src: tileStoneImage, category: 'interior', title: 'Marble Stone — Tile & Grout Restoration', aspect: 'landscape' },
  { src: newStoneImage, category: 'interior', title: 'Polished Stone — Floor Refinishing & Sealing', aspect: 'landscape' },
  {
    src: 'https://images.unsplash.com/photo-1763979628017-ea650631b0be?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1200',
    category: 'interior',
    title: 'Luxury Leather Sofa — Cleaning & Conditioning',
    aspect: 'landscape',
  },
  {
    src: 'https://images.unsplash.com/photo-1658501238841-da09649a94f3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsZWF0aGVyJTIwc29mYSUyMGludGVyaW9yJTIwZGVlcCUyMGNsZWFuJTIwY29uZGl0aW9uaW5nfGVufDF8fHx8MTc3MzE5ODM1Mnww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    category: 'interior',
    title: 'Upholstery — Fabric Deep Clean & Protection',
    aspect: 'landscape',
  },
  {
    src: 'https://images.unsplash.com/photo-1678686425633-84fc103e2727?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob21lJTIwaW50ZXJpb3IlMjBsaXZpbmclMjByb29tJTIwZGFyayUyMG1vb2R5JTIwZWxlZ2FudHxlbnwxfHx8fDE3NzMxOTg0NzB8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    category: 'interior',
    title: 'Luxury Residence — Full Interior Service',
    aspect: 'landscape',
  },
  {
    src: 'https://images.unsplash.com/photo-1766246099181-2055091f8721?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1200',
    category: 'auto',
    title: 'Supercar — Detail Studio Session',
    aspect: 'landscape',
  },
  { src: yachtDeckImage, category: 'auto', title: 'Luxury Yacht — Stern Deck & Marine Detailing', aspect: 'portrait' },
  { src: yachtProfileImage, category: 'auto', title: 'Luxury Yacht — Hull Polish & Full Protection', aspect: 'portrait' },
];

const categories = [
  { value: 'all', label: 'All Work' },
  { value: 'auto', label: 'Auto Detailing' },
  { value: 'interior', label: 'Interior Services' },
];

export function Gallery() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filtered = activeCategory === 'all'
    ? allImages
    : allImages.filter((img) => img.category === activeCategory);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const prevImage = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + filtered.length) % filtered.length);
  };

  const nextImage = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % filtered.length);
  };

  return (
    <Box sx={{ background: '#080808', minHeight: '100vh' }}>
      {/* Hero */}
      <Box
        sx={{
          pt: { xs: 18, md: 22 },
          pb: { xs: 8, md: 10 },
          borderBottom: `1px solid ${alpha(gold, 0.1)}`,
        }}
      >
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
              Our Portfolio
            </Typography>
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: '2.5rem', md: '4.5rem' }, color: '#F2EDE4', mb: 3 }}
            >
              Gallery
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: alpha('#F2EDE4', 0.6), maxWidth: 560, lineHeight: 2 }}
            >
              A selection of our work — each project a testament to the standard of excellence Lindsey Enterprises brings to every engagement.
            </Typography>
          </motion.div>

          {/* Category Filter */}
          <Box sx={{ display: 'flex', gap: 4, mt: 6, borderBottom: `1px solid ${alpha('#F2EDE4', 0.08)}` }}>
            {categories.map((cat) => {
              const isActive = activeCategory === cat.value;
              return (
                <Box
                  key={cat.value}
                  component="button"
                  onClick={() => setActiveCategory(cat.value)}
                  sx={{
                    background: 'none',
                    border: 'none',
                    borderBottom: isActive ? `1px solid ${gold}` : '1px solid transparent',
                    mb: '-1px',
                    px: 0,
                    py: 1.5,
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    color: isActive ? gold : alpha('#F2EDE4', 0.4),
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: isActive ? gold : alpha('#F2EDE4', 0.75),
                    },
                  }}
                >
                  {cat.label}
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* Grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Box
              sx={{
                columns: { xs: 1, sm: 2, md: 3 },
                columnGap: '4px',
                '& > *': { breakInside: 'avoid', mb: '4px' },
              }}
            >
              {filtered.map((img, i) => (
                <motion.div
                  key={img.src + activeCategory}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                >
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Open gallery image: ${img.title}`}
                    onClick={() => openLightbox(i)}
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      display: 'block',
                      width: '100%',
                      border: 0,
                      p: 0,
                      background: 'transparent',
                      textAlign: 'left',
                      '&:hover .gallery-overlay': { opacity: 1 },
                      '&:hover img': { transform: 'scale(1.04)' },
                    }}
                  >
                    <Box
                      component="img"
                      src={img.src}
                      alt={img.title}
                      sx={{
                        width: '100%',
                        display: 'block',
                        transition: 'transform 0.5s ease',
                        filter: 'brightness(0.85)',
                      }}
                    />
                    <Box
                      className="gallery-overlay"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(to top, rgba(4,4,4,0.85) 0%, transparent 60%)`,
                        opacity: 0,
                        transition: 'opacity 0.4s ease',
                        display: 'flex',
                        alignItems: 'flex-end',
                        p: 3,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#F2EDE4',
                          fontSize: '0.78rem',
                          fontFamily: '"Cormorant Garamond", serif',
                          fontWeight: 400,
                          fontStyle: 'italic',
                          lineHeight: 1.4,
                        }}
                      >
                        {img.title}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </AnimatePresence>
      </Container>

      {/* Lightbox */}
      <Modal
        open={lightboxIndex !== null}
        onClose={closeLightbox}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(4,4,4,0.97)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {lightboxIndex !== null && (
            <>
              <Box
                component="img"
                src={filtered[lightboxIndex].src}
                alt={filtered[lightboxIndex].title}
                sx={{
                  maxWidth: '90vw',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#F2EDE4', 0.6),
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: '1rem',
                    fontStyle: 'italic',
                  }}
                >
                  {filtered[lightboxIndex].title}
                </Typography>
              </Box>

              {/* Close */}
              <IconButton
                aria-label="Close gallery image"
                onClick={closeLightbox}
                sx={{
                  position: 'fixed',
                  top: 20,
                  right: 20,
                  color: alpha('#F2EDE4', 0.7),
                  background: alpha('#000', 0.5),
                  borderRadius: 0,
                  '&:hover': { color: gold },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Prev/Next */}
              <IconButton
                aria-label="Show previous gallery image"
                onClick={prevImage}
                sx={{
                  position: 'fixed',
                  left: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: alpha('#F2EDE4', 0.7),
                  background: alpha('#000', 0.5),
                  borderRadius: 0,
                  '&:hover': { color: gold },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: '2rem' }} />
              </IconButton>
              <IconButton
                aria-label="Show next gallery image"
                onClick={nextImage}
                sx={{
                  position: 'fixed',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: alpha('#F2EDE4', 0.7),
                  background: alpha('#000', 0.5),
                  borderRadius: 0,
                  '&:hover': { color: gold },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: '2rem' }} />
              </IconButton>

              {/* Counter */}
              <Box sx={{ position: 'fixed', bottom: 30, left: '50%', transform: 'translateX(-50%)' }}>
                <Typography variant="overline" sx={{ color: alpha('#F2EDE4', 0.4), fontSize: '0.6rem' }}>
                  {lightboxIndex + 1} / {filtered.length}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
}
